import { useMemo, useCallback } from 'react';
import useMyBudget from 'hooks/budget/useMyBudget';
import pick from 'lodash/pick';
import LoadingOverlay from 'components/common/LoadingOverlay';
import { useHistory } from 'react-router-dom';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { type IntlShape, useIntl } from 'react-intl';
import useUpdateBudget from 'hooks/budget/useUpdateBudget';
import { toast } from 'react-toastify';
import routePaths from 'router/route-paths';
import BudgetCalculatorEditForm, {
  type TBudgetEditValues,
} from 'components/budget/BudgetCalculatorEditForm';
import PageTitle from 'components/common/PageTitle';
import round from 'lodash/round';
import WarningMessage from 'components/common/WarningMessage';
import type { FormikHelpers, FormikProps } from 'formik';
import type { TBudgetCalculation, TMyBudget } from 'components/budget/form/types';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';
import { userInputOrCalculatedValue } from 'components/budget/form/utils';

const monthlyExpensesKeys: (keyof TBudgetCalculation)[] = [
  'monthly_expenses',
  'monthly_expenses_partner',
  'monthly_expenses_kids',
  'costs_for_repair',
  'technology',
  'luxuries',
  'socializing',
  'hobbies',
  'taking_a_break',
  'new_car',
  'pension_and_insurances',
];

const isCalculationKey = (
  calculation: TBudgetCalculation,
  key: string,
): key is keyof TBudgetCalculation => key in calculation;

/** `round(get(myBudget, 'calculation[key].calculated_value'))`: NaN for a missing value. */
const roundedCalculatedValue = (myBudget: TMyBudget, key: string) => {
  if (!isCalculationKey(myBudget.calculation, key)) return Number.NaN;
  return round(myBudget.calculation[key].calculated_value);
};

const useUpdateBudgetHandler = (myBudget: TMyBudget) => {
  const updateBudget = useUpdateBudget();
  const history = useHistory();

  const handleSubmit = useCallback(
    async (
      variables: TBudgetEditValues,
      { setSubmitting }: FormikHelpers<TBudgetEditValues>,
      formik: FormikProps<TBudgetEditValues> | null,
    ) => {
      setSubmitting(true);
      try {
        const changedVariables = Object.keys(variables).filter(
          (key) => roundedCalculatedValue(myBudget, key) !== variables[key],
        );

        if (requireValue(formik, 'dirty').dirty)
          await updateBudget(pick(variables, changedVariables));

        history.push(routePaths.lifestyleOverview);
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [history, myBudget, updateBudget],
  );
  return handleSubmit;
};

// A GraphQL Float is always finite (the former code kept a non-finite value as its object).
function getInitialValues(lifestyleCalculations?: TMyBudget | null) {
  if (!lifestyleCalculations) return {};
  const allowedValues: TBudgetEditValues = {};

  monthlyExpensesKeys.forEach((key) => {
    const item = lifestyleCalculations.calculation[key];
    if (item.calculated_value === 0 && !Number.isFinite(item.user_input)) return;
    if (Number.isFinite(item.calculated_value)) {
      const value = userInputOrCalculatedValue(item);
      allowedValues[key] = round(value);
    }
  });
  return allowedValues;
}

interface ILifestyleEditMonthlyExpensesValuesProps {
  lifestyleCalculations: TMyBudget;
  t: IntlShape['formatMessage'];
}

const LifestyleEditMonthlyExpensesValues = ({
  lifestyleCalculations,
  t,
}: ILifestyleEditMonthlyExpensesValuesProps) => {
  const onSubmit = useUpdateBudgetHandler(lifestyleCalculations);

  const initialValues = useMemo(
    () => getInitialValues(lifestyleCalculations),
    [lifestyleCalculations],
  );

  return (
    <Grid>
      <Row between="xs" center="md">
        <Col md={8} lg={12}>
          <PageTitle>{t({ id: 'page_titles.edit_budget' })}</PageTitle>
        </Col>
      </Row>
      <BudgetCalculatorEditForm {...{ onSubmit, initialValues }} />
    </Grid>
  );
};

export default () => {
  const [lifestyleCalculations, loading] = useMyBudget();
  const { formatMessage: t } = useIntl();

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!lifestyleCalculations) {
    return (
      <Row center="xs">
        <Col md={8} lg={6}>
          <WarningMessage
            title={t({ id: 'page_titles.edit_budget' })}
            message={t({ id: 'budget.edit.complete_budget_step' })}
            btnLabel={t({ id: 'budget.edit.complete_budget_step_btn' })}
            btnLink={routePaths.lifestyleOverview}
          />
        </Col>
      </Row>
    );
  }
  return <LifestyleEditMonthlyExpensesValues {...{ lifestyleCalculations, t }} />;
};
